/* eslint-disable consistent-return */
import dayjs from 'dayjs';
import { friendlyError } from '@/utils/errorMessage';
import membersService from '@/services/members.service';
import authService from '@/services/auth.service';
import documentsService from '@/services/documents.service';
import eventsService from '@/services/events.service';
import printService from '@/services/print.service';
import lookupsService from '@/services/lookups.service';
import bootstrapBranchContext from '@/utils/branchContextBootstrap';

// Shared across all components — dedups concurrent requests for the same image id.
const imageRequestsInProgress = Object.create(null);

export default {
  methods: {
    // -----------------------------------------------------------------------
    // Errors
    // -----------------------------------------------------------------------
    // Report a failed request to the user. Pass what they were trying to do — "load the
    // open-balance report", "save the cash box" — and the toast names it instead of shrugging.
    // Technical detail goes to the console, never to the screen.
    showError(error, entity, verb = 'load') {
      // eslint-disable-next-line no-console
      console.error(error);
      // Claim the failure so the response interceptor's own, vaguer toast stands down.
      if (error && typeof error === 'object') error.handled = true;
      this.$toast.error(friendlyError(error, this.$t.bind(this), {
        entity,
        verb,
        locale: this.$i18n && this.$i18n.locale,
      }));
    },

    // -----------------------------------------------------------------------
    // Access gating — single source of truth (FITNET-103)
    // The backend already returns feature-filtered permissions and resolver-backed
    // features/toggles, so these just read the stored arrays. Use these instead of
    // reaching into the store inline so gating logic lives in one place.
    // -----------------------------------------------------------------------
    hasFeature(featureCode) {
      const tenantSetting = this.$store.state.settings.tenantSetting;
      return !!(tenantSetting
        && tenantSetting.tenantActiveFeatures
        && tenantSetting.tenantActiveFeatures.includes(featureCode));
    },
    can(actionCode) {
      const userAuthActions = this.$store.state.auth.userAuthActions;
      return !!(userAuthActions && userAuthActions.some((i) => i.actionCode === actionCode));
    },

    // -----------------------------------------------------------------------
    // Localization utilities
    // -----------------------------------------------------------------------
    localizeWith(utilFn, value, extraOptions = {}) {
      return utilFn(value, {
        locale: this.$i18n.locale,
        t: this.$t.bind(this),
        ...extraOptions,
      });
    },

    // -----------------------------------------------------------------------
    // Date utilities (pure helpers — no API calls)
    // -----------------------------------------------------------------------
    changeDateTimeFormat(date, dateFormat = true, timeFormat = false) {
      const dateTimeFormat = (dateFormat ? this.$store.state.settings.tenantSetting.dateFormat : '')
        + (dateFormat ? ' ' : '')
        + (timeFormat ? this.$store.state.settings.tenantSetting.timeFormat : '');

      return dayjs().format(new Date(date), dateTimeFormat);
    },

    formatDate(datetime, dateFormat = true, timeFormat = false) {
      if (!datetime) {
        return 'NA';
      }
      const date = new Date(datetime);

      const hours24 = date.getHours();
      const hours12 = hours24 % 12 || 12;
      const ampm = hours24 < 12 ? 'AM' : 'PM';

      const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthsLong = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

      const map = {
        yyyy: date.getFullYear(),
        YYYY: date.getFullYear(),
        MMMM: monthsLong[date.getMonth()],
        MMM: monthsShort[date.getMonth()],
        MM: String(date.getMonth() + 1).padStart(2, '0'),
        dd: String(date.getDate()).padStart(2, '0'),
        HH: String(hours24).padStart(2, '0'),
        hh: String(hours12).padStart(2, '0'),
        mm: String(date.getMinutes()).padStart(2, '0'),
        ss: String(date.getSeconds()).padStart(2, '0'),
        tt: ampm,
      };

      const format = (dateFormat ? this.$store.state.settings.tenantSetting.dateFormat : '')
        + (dateFormat ? ' ' : '')
        + (timeFormat ? this.$store.state.settings.tenantSetting.timeFormat : '');

      return format.replace(/yyyy|YYYY|MMMM|MMM|MM|dd|HH|hh|mm|ss|tt/g, (match) => map[match]);
    },

    addDaysToDate(date, days) {
      const result = new Date(date);
      result.setDate(result.getDate() + days);
      return result;
    },

    addMonthsToDate(date, months) {
      const result = new Date(date);
      result.setMonth(result.getMonth() + months);
      return result;
    },

    convertToISOFormat(dateString) {
      const date = new Date(dateString);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}T00:00:00.000[Z]`;
    },

    dateDefaultFormatDisplay(date, displayTime = false) {
      if (displayTime) {
        return this.$date(date).format('MMM Do YYYY HH:mm');
      }
      return this.$date(date).format('MMM Do YYYY');
    },

    removeTimezoneFromDateToISOFormatString(date) {
      return this.$date(date).format('YYYY-MM-DDTHH:mm:ss[Z]');
    },

    // -----------------------------------------------------------------------
    // Object utilities
    // -----------------------------------------------------------------------
    cleanEmptyAttrFromObj(obj) {
      // Preserve arrays — spreading one into an object would turn ["2"] into {"0":"2"},
      // which the backend can't bind to a List<> (whole request body then fails to deserialize).
      if (Array.isArray(obj)) {
        return obj.map((item) => (typeof item === 'object' && item !== null
          ? this.cleanEmptyAttrFromObj(item)
          : item));
      }
      const newObj = { ...obj };
      Object.keys(newObj).forEach((key) => {
        if (newObj[key] === null || newObj[key] === undefined || newObj[key] === '') {
          delete newObj[key];
        }
      });
      Object.keys(newObj).forEach((key) => {
        if (typeof newObj[key] === 'object' && newObj[key] !== null) {
          newObj[key] = this.cleanEmptyAttrFromObj(newObj[key]);
        }
      });
      return newObj;
    },

    // -----------------------------------------------------------------------
    // Tenant currency
    // -----------------------------------------------------------------------
    // The tenant's currency id: prefer the scalar defaultCurrency (0 is a valid id),
    // fall back to the currency object's id so it isn't dropped when the scalar is empty.
    tenantCurrencyId() {
      const setting = (this.$store && this.$store.state.settings.tenantSetting) || {};
      if (setting.defaultCurrency !== null && setting.defaultCurrency !== undefined && setting.defaultCurrency !== '') {
        return setting.defaultCurrency;
      }
      return (setting.currencyData && setting.currencyData.id) || undefined;
    },
    // Null-safe currency short code for display (tenants with incomplete settings).
    tenantCurrencyShortcut() {
      const setting = (this.$store && this.$store.state.settings.tenantSetting) || {};
      return (setting.currencyData && setting.currencyData.shortcut) || '';
    },

    // -----------------------------------------------------------------------
    // DOM utilities
    // -----------------------------------------------------------------------
    findClassNameOrIdOnPathFromEvent(eventObj, findValue) {
      const path = eventObj && (eventObj.composedPath ? eventObj.composedPath() : eventObj.path);
      if (path && path.length > 0 && findValue) {
        return path.some((i) => (i.className && typeof i.className === 'string' && i.className.includes(findValue))
          || (i.id && i.id.includes(findValue)));
      }
      return false;
    },

    // -----------------------------------------------------------------------
    // Relative time (i18n-aware)
    // -----------------------------------------------------------------------
    getTimeSince(dateValue) {
      if (!dateValue) {
        return this.$t('generalMessages.NA');
      }

      const date = this.$date(dateValue);
      const time = this.$date().from(date);

      if (time.includes('month')) {
        if (time === 'in a month') {
          return this.$t('generalMessages.inMonth');
        }
        const numbers = time.match(/\d+/g).map(Number);
        return `${this.$t('generalMessages.in')} ${this.$n(numbers)} ${this.$t('generalMessages.inMonths')}`;
      }

      if (time.includes('seconds') && time === 'in a few seconds') {
        return this.$t('generalMessages.inafewseconds');
      }

      if (time.includes('minute')) {
        if (time === 'in a minute') {
          return this.$t('generalMessages.inaminute');
        }
        const numbers = time.match(/\d+/g).map(Number);
        return `${this.$t('generalMessages.in')} ${this.$n(numbers)} ${this.$t('generalMessages.minutes')}`;
      }

      if (time.includes('hour')) {
        if (time === 'in an hour') {
          return this.$t('generalMessages.inanhour');
        }
        const numbers = time.match(/\d+/g).map(Number);
        return `${this.$t('generalMessages.in')} ${this.$n(numbers)} ${this.$t('generalMessages.hours')}`;
      }

      if (time.includes('day')) {
        if (time === 'in a day') {
          return this.$t('generalMessages.inaday');
        }
        const numbers = time.match(/\d+/g).map(Number);
        return `${this.$t('generalMessages.in')} ${this.$n(numbers)} ${this.$t('generalMessages.daystime')}`;
      }

      if (time.includes('year')) {
        if (time === 'in a year') {
          return this.$t('generalMessages.inayear');
        }
        const numbers = time.match(/\d+/g).map(Number);
        return `${this.$t('generalMessages.in')} ${this.$n(numbers)} ${this.$t('generalMessages.years')}`;
      }

      return time;
    },

    // -----------------------------------------------------------------------
    // API methods — delegated to services (backward compatible)
    // -----------------------------------------------------------------------
    async downloadImage(id) {
      return documentsService.downloadImage(id);
    },

    fetchCachedImage(id, getFallbackUrl) {
      if (!id || id <= 0) return undefined;

      const cache = this.$store.state.settings.images;
      if (Object.prototype.hasOwnProperty.call(cache, id)) {
        return cache[id].data;
      }

      if (imageRequestsInProgress[id]) return undefined;
      imageRequestsInProgress[id] = true;

      this.downloadImage(id)
        .then((data) => {
          this.$store.commit('updateImages', { id, data });
        })
        .catch(() => {
          let fallback = null;
          try {
            if (typeof getFallbackUrl === 'function') {
              fallback = getFallbackUrl() || null;
            }
          } catch (e) {
            fallback = null;
          }
          this.$store.commit('updateImages', { id, data: fallback });
        })
        .finally(() => {
          delete imageRequestsInProgress[id];
        });

      return undefined;
    },

    async getMemberById(memberId) {
      this.$store.commit('changeShowLoader', true);
      try {
        return await membersService.getMemberById(memberId);
      } catch (err) {
        this.showError(err, this.$t('errors.entities.item'), 'load');
      } finally {
        this.$store.commit('changeShowLoader', false);
      }
    },

    async getFinancialStatusByMemberIdMixin(memberId, from, to) {
      this.$store.commit('changeShowLoader', true);
      try {
        return await membersService.getFinancialStatus(memberId, from, to);
      } catch (err) {
        this.showError(err, this.$t('errors.entities.item'), 'load');
      } finally {
        this.$store.commit('changeShowLoader', false);
      }
    },

    async eventDeleteMixin(eventId, callback) {
      await this.$confirm(this.$t('events.deleteEventConfirmation'), undefined, undefined, {
        confirmButtonText: this.$t('generalMessages.Ok'),
        cancelButtonText: this.$t('generalMessages.cancel'),
      });
      this.$store.commit('changeShowLoader', true);
      try {
        await eventsService.deleteEvent(eventId);
        this.$toast.success(this.$t('events.successDelete'));
        callback();
      } catch (err) {
        this.showError(err, this.$t('errors.entities.item'), 'save');
      } finally {
        this.$store.commit('changeShowLoader', false);
      }
    },

    async classSeriesDeleteMixin(classId, classSeriesId, callback) {
      await this.$confirm(this.$t('events.classSerriesDelete'), undefined, undefined, {
        confirmButtonText: this.$t('generalMessages.Ok'),
        cancelButtonText: this.$t('generalMessages.cancel'),
      });
      this.$store.commit('changeShowLoader', true);
      try {
        await eventsService.deleteClassSeries(classId, classSeriesId);
        this.$toast.success(this.$t('events.successDeleteSeries'));
        callback();
      } catch (err) {
        this.showError(err, this.$t('errors.entities.item'), 'save');
      } finally {
        this.$store.commit('changeShowLoader', false);
      }
    },

    async printReceipt(itemId) {
      this.$store.commit('changeShowLoader', true);
      try {
        await printService.printReceipt(itemId);
      } catch (err) {
        this.showError(err, this.$t('errors.entities.item'), 'prepare');
      } finally {
        this.$store.commit('changeShowLoader', false);
      }
    },

    async getReceiptData(saleOrderId) {
      this.$store.commit('changeShowLoader', true);
      try {
        await printService.getSaleOrderReceipt(saleOrderId);
      } catch (err) {
        this.showError(err, this.$t('errors.entities.item'), 'load');
      } finally {
        this.$store.commit('changeShowLoader', false);
      }
    },

    async printMembershipEnrollment(itemId) {
      this.$store.commit('changeShowLoader', true);
      try {
        await printService.printMembershipEnrollment(itemId);
      } catch (err) {
        this.showError(err, this.$t('errors.entities.item'), 'prepare');
      } finally {
        this.$store.commit('changeShowLoader', false);
      }
    },

    async getLoggedInUserPermissions() {
      this.$store.commit('changeShowLoader', true);
      try {
        const data = await authService.getLoggedInUserPermissions();
        this.$store.commit('changeUserAuthActions', data);
      } catch (err) {
        this.showError(err, this.$t('errors.entities.item'), 'load');
      } finally {
        this.$store.commit('changeShowLoader', false);
      }
    },

    // Called from both entry points -- App.vue's created hook and the sign-in -- because only one
    // of the two ever runs with a session behind it. initializeBranchContext is idempotent.
    async bootstrapBranchContext() {
      await bootstrapBranchContext({
        userData: this.$store.state.auth.userData,
        hasMultipleBranches: this.hasFeature('multiple-branches'),
        loadAccess: () => lookupsService.getMyBranchAccess(),
        loadBranches: () => lookupsService.getBranches(),
        dispatch: (action, payload) => this.$store.dispatch(action, payload),
      });
    },
  },
};
